/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Icon, Menu, Popup } from '@mairistem/composants';
import { ListeTable } from '@mairistem/liste';

import type { MentionActe } from '@/types';
import { formatDateSeule, rtfToText } from '@/utils';

/**
 * Libellé de la catégorie ANTS, dans la forme employée à la saisie.
 *
 * Exporté pour être testable seul : `ListeTable.Cell` est simulé dans les
 * tests et ne rend pas l'attribut `title` qui le porte.
 */
export const libelleAnts = (item: Partial<MentionActe>) =>
  _.compact([item.codeCategorieAnts, item.typeAnts, item.intituleAnts]).join(
    ' - ',
  ) || undefined;

interface MentionListeTableRowProps {
  item: Partial<MentionActe>;
  invalidate?: () => void;
  onEdit: (mention: Partial<MentionActe>) => void;
  onDelete: (mention: Partial<MentionActe>) => void;
}

const MentionListeTableRow = (props: MentionListeTableRowProps) => {
  const { item, invalidate, onEdit, onDelete } = props;

  // biome-ignore lint/correctness/useExhaustiveDependencies: no need
  React.useEffect(() => {
    invalidate?.();
  }, []);

  if (!item) return null;

  return (
    <ListeTable.Row key={item.identifiant}>
      <ListeTable.Cell name="numero">{item.numero}</ListeTable.Cell>
      <ListeTable.Cell name="dateApposition">
        {formatDateSeule(item.dateApposition)}
      </ListeTable.Cell>
      <ListeTable.Cell name="titre">{item.titre}</ListeTable.Cell>
      <ListeTable.Cell
        name="texte"
        style={{ whiteSpace: 'pre-wrap', minWidth: 0, wordBreak: 'break-word' }}
      >
        {rtfToText(item.texte)}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="codeMentionAnts"
        style={{ whiteSpace: 'normal', minWidth: 0, wordBreak: 'break-word' }}
        /*
          Le libellé complet de la catégorie ANTS, tel que le dropdown de
          saisie l'affiche : la cellule est trop étroite pour le porter.
        */
        // @ts-expect-error `title` absent du type de la cellule, transmis au DOM par la lib
        title={libelleAnts(item)}
      >
        {/*
          Code de catégorie, comme dans l'ETCV ; brut s'il est inconnu du
          référentiel, rien pour 0 / -1 (« pas de code » dans l'historique).
        */}
        <div>
          {item.codeCategorieAnts ??
            (Number(item.codeMentionAnts) > 0 ? item.codeMentionAnts : null)}
        </div>
        <div>{item.intituleAnts ?? item.libelle}</div>
      </ListeTable.Cell>
      <ListeTable.Cell name="actions" textAlign="center">
        <Popup
          basic
          on="click"
          pinned
          flowing
          position="bottom right"
          style={{ padding: 0 }}
          trigger={<Icon link name="bars" color="blue" />}
          content={
            <Menu vertical fluid>
              <Menu.Item onClick={() => onEdit(item)}>
                Modifier la mention
              </Menu.Item>
              <Menu.Item
                data-testid="mention-supprimer"
                onClick={() => onDelete(item)}
              >
                Supprimer la mention
              </Menu.Item>
            </Menu>
          }
        />
      </ListeTable.Cell>
    </ListeTable.Row>
  );
};

export default MentionListeTableRow;
